// src/components/EditOrderPage.jsx
import axios from 'axios';
import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import {
    FaFileAlt, // For Sub Total icon
    FaPercentage, // For Discount icon
    FaTruck, // For Delivery Charges and Estimated Delivery icon
    FaMoneyBillAlt, // For Estimated Tax icon
    FaShieldAlt // For Security Message icon
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';

export default function EditOrderPage() {
    const { id } = useParams();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
    const [isLoading, setIsLoading] = useState(true);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        yourEmail: '',
        phoneNumber: '',
        shippingAddress: '',
        zipCode: '',
        city: '',
        country: '',
        state: '',
        town: '',
        famousSpot: ''
    });
    const [orderSummary, setOrderSummary] = useState({
        subTotal: 0,
        discount: 0,
        deliveryCharges: 0,
        estimatedTax: 0,
        totalAmount: 0
    });
    const [orderItems, setOrderItems] = useState([]);
    const [orderStatus, setOrderStatus] = useState('');
    const [estimatedDelivery, setEstimatedDelivery] = useState('');

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            setIsSmallMobile(window.innerWidth <= 480);
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Call once to set initial state
        
        // Fetch order data from API
        fetchOrderData();
        
        return () => window.removeEventListener('resize', handleResize);
    }, [id]);

    // Fetch order data from API
    const fetchOrderData = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`https://api.alburaqscents.com/v1/order/detail/${id}`);
            if (!response.ok) {
                throw new Error('Failed to fetch order details');
            }
            const data = await response.json();
            
            if (data.success) {
                const orderData = data.data;
                
                // Extract first and last name from full name
                const nameParts = orderData.customerId.name.split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts.slice(1).join(' ') || '';

                // Calculate order summary
                const subTotal = orderData.items.reduce((sum, item) => sum + (item.priceAtPurchase * item.quantity), 0);
                const discount = subTotal - orderData.totalAmount; // Calculate discount based on difference
                const deliveryCharges = 0; // Free delivery
                const estimatedTax = Math.round(subTotal * 0.155); // 15.5% tax
                const totalAmount = orderData.totalAmount;

                setFormData({
                    firstName,
                    lastName,
                    yourEmail: orderData.customerId.email,
                    phoneNumber: orderData.shippingAddress.phoneNumber,
                    shippingAddress: orderData.shippingAddress.address,
                    city: orderData.shippingAddress.city,
                    country: orderData.shippingAddress.country,
                    state: orderData.shippingAddress.state,
                    town: orderData.shippingAddress.town,
                    famousSpot: orderData.shippingAddress.famousSpot,
                    zipCode: '' // Not provided in API, leave empty
                });

                setOrderSummary({
                    subTotal,
                    discount,
                    deliveryCharges,
                    estimatedTax,
                    totalAmount
                });

                setOrderItems(orderData.items);
                setOrderStatus(orderData.orderStatus);
                
                // Set estimated delivery date (you might want to calculate this based on order date)
                const orderDate = new Date(orderData.createdAt);
                const deliveryDate = new Date(orderDate);
                deliveryDate.setDate(orderDate.getDate() + 7); // Adding 7 days as estimated delivery
                setEstimatedDelivery(deliveryDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }));
            }
        } catch (error) {
            console.error('Error fetching order data:', error);
            toast.error('Failed to load order details');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const navigate = useNavigate();

    const handleUpdateOrder = async () => {
  setIsSubmitting(true);
  setSubmitError(null);
  
  try {
    // Prepare the data to be sent to the customer address update endpoint
    const updatedData = {
      email: formData.yourEmail,
      city: formData.city,
      country: formData.country,
      state: formData.state,
      town: formData.town,
      address: formData.shippingAddress,
      famousSpot: formData.famousSpot,
      phoneNumber: formData.phoneNumber,
      name: `${formData.firstName} ${formData.lastName}`,
    };

    // First update the customer's address
    const customerResponse = await axios.patch(`https://api.alburaqscents.com/v1/customer/address`, updatedData);

    if (!customerResponse.data.success) {
      throw new Error(customerResponse.data.message);
    }

    // Then update the order's shipping address to match
    const orderResponse = await axios.put(`https://api.alburaqscents.com/v1/order/${id}/shipping-address`, {
      address: formData.shippingAddress,
      city: formData.city,
      country: formData.country,
      state: formData.state,
      town: formData.town,
      famousSpot: formData.famousSpot,
      phoneNumber: formData.phoneNumber
    }, {
      headers: {
        'Content-Type': 'application/json',
        // Add authorization header if needed
        // 'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!orderResponse.data.success) {
      throw new Error(orderResponse.data.message || 'Failed to update order shipping address');
    }

    setSubmitSuccess(true);
    toast.success('Address updated successfully for both customer and order!');
    navigate('/orders/list');
  } catch (error) {
    console.error('Error updating address:', error);
    setSubmitError(error.message);
    toast.error(error.message || 'Failed to update address');
  } finally {
    setIsSubmitting(false);
  }
};

    const handleCancel = () => {
        if (window.confirm('Are you sure you want to cancel? All changes will be lost.')) {
            fetchOrderData(); // Reset to original data
        }
    };

     const styles = {
        // --- Main Layout & Global Styles ---
        mainWrapper: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: '100vh',
            width: '100%',
            backgroundColor: 'transparent', // Main background color from image
            padding: isSmallMobile ? '15px' : (isMobile ? '30px' : '20px'), // Increased overall padding
            boxSizing: 'border-box',
            fontFamily: 'Arial, sans-serif', // Using Arial for a more precise match
            overflowY: 'auto',
            overflowX: 'hidden',
        },
        pageTitle: {
            fontSize: '15px', // Larger font size for title
            fontWeight: 'bold',
            color: '#000', // Black for "Edit Order" heading
            marginBottom: '30px', // More space below the title
            width: '100%',
            maxWidth: '1200px', // Match content max width
            textAlign: 'left',
            paddingLeft: '20px', // Aligned with the card content below
            boxSizing: 'border-box',
        },
        pageContent: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', // Two columns for desktop, single for mobile
            gap: '30px', // Increased gap between columns for more space
            width: '100%',
            maxWidth: '1200px', // Max width for the content columns
        },
        column: {
            display: 'flex',
            flexDirection: 'column',
            gap: '30px', // Increased gap between cards within a column
        },
        card: {
            backgroundColor: '#000', // Black background for cards
            borderRadius: '12px', // Slightly more rounded corners
            padding: '30px', // Generous padding for height and spaciousness
            boxShadow: '0 4px 10px rgba(0,0,0,0.5)', // Slightly stronger shadow
            color: '#E5D0A5', // Beige text
        },
        cancelButton: {
  backgroundColor: '#333',
  color: '#E5D0A5',
  border: 'none',
},
cancelButtonHover: {
  backgroundColor: '#555',
},
updateButton: {
  backgroundColor: '#D4AF37',
  color: '#000',
  border: 'none',
},
updateButtonHover: {
  backgroundColor: '#C0A020',
},
        cardHeader: {
            fontSize: '17px',
            fontWeight: 'bold',
            marginBottom: '20px', // More space below card header
            color: '#E5D0A5',
        },
        // --- Top Black Box (Estimated shipping date) ---
        shippingDateBox: {
            backgroundColor: '#000',
            borderRadius: '12px',
            padding: '20px 30px', // Generous padding
            color: '#D4AF37', // Gold text
            fontSize: '16px', // Larger font size
            fontWeight: 'bold', // Bold text
            textAlign: 'center',
            marginBottom: '30px', // More space below this box
            width: '100%',
            maxWidth: '1200px', // Match main content width
            boxSizing: 'border-box',
        },
        // --- Personal Details & Shipping Details Cards ---
        sectionTitle: {
            fontSize: '15px', // Slightly larger font for section titles
            fontWeight: 'bold',
            color: '#D4AF37', // Gold for section titles
            marginBottom: '20px', // More space below section title
        },
        formGrid: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', // Two columns for desktop, single for mobile
            gap: '20px', // Increased gap between form inputs
        },
        formGroup: {
            display: 'flex',
            flexDirection: 'column',
        },
        inputLabel: {
            fontSize: '13px', // Slightly larger font for labels
            color: '#E5D0A5',
            marginBottom: '8px', // More space below label
        },
        textInput: {
            backgroundColor: '#000',
            border: '1px solid #D4AF37', // Gold border
            borderRadius: '8px', // Slightly more rounded
            padding: '12px 15px', // Generous padding for height
            color: '#E5D0A5',
            fontSize: '15px', // Slightly larger font
            outline: 'none',
            width: '100%',
            boxSizing: 'border-box',
        },
        textArea: {
            backgroundColor: '#000',
            border: '1px solid #D4AF37',
            borderRadius: '8px',
            padding: '12px 15px',
            color: '#E5D0A5',
            fontSize: '15px',
            outline: 'none',
            width: '100%',
            minHeight: '100px', // Increased min height for textarea
            resize: 'vertical',
            boxSizing: 'border-box',
        },
        // --- Security Message Box ---
        securityMessageBox: {
            backgroundColor: '#EED992', // Light gold/beige background
            borderRadius: '8px',
            padding: '15px 20px', // Generous padding
            marginTop: '30px', // More space above this box
            display: 'flex',
            alignItems: 'center',
            gap: '15px', // More space between icon and text
            color: '#000', // Black text
            fontSize: '14px', // Slightly larger font
            fontWeight: 'normal',
        },
        securityIcon: {
            color: '#000', // Black icon
            fontSize: '15px', // Larger icon size
        },

        // --- Right Column - Promo and Summary ---
        promoCodeCard: {
            backgroundColor: '#000',
            borderRadius: '12px',
            padding: '30px', // Consistent generous padding
            color: '#E5D0A5',
        },
        promoCodeHeader: {
            fontSize: '15px',
            fontWeight: 'bold',
            marginBottom: '20px',
            color: '#E5D0A5',
        },
        promoInputGroup: {
            display: 'flex',
            gap: '10px',
        },
        // Reusing textInput for promoInput for consistency in height and style
        applyButton: {
            backgroundColor: '#D4AF37',
            color: '#000',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 20px', // Generous padding for height
            fontSize: '15px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
        },
        applyButtonHover: {
            backgroundColor: '#C0A020',
        },
        orderSummaryCard: {
            backgroundColor: '#000',
            borderRadius: '12px',
            padding: '30px', // Consistent generous padding
            color: '#E5D0A5',
        },
        orderSummaryHeader: {
            fontSize: '15px',
            fontWeight: 'bold',
            marginBottom: '20px',
            color: '#E5D0A5',
        },
        summaryRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px', // More space between summary rows
            fontSize: '15px',
            color: '#E5D0A5',
        },
        summaryLabel: {
            color: '#E5D0A5',
            fontWeight: 'normal',
            display: 'flex',
            alignItems: 'center',
            gap: '12px', // More space between icon and text
        },
        summaryValue: {
            fontWeight: 'normal',
            color: '#E5D0A5',
        },
        summaryDivider: {
            borderBottom: '1px solid #444', // Slightly darker divider
            margin: '15px 0', // More margin for dividers
        },
        totalAmountRow: {
            fontSize: '17px', // Larger font for total amount
            fontWeight: 'bold',
            color: '#E5D0A5',
            textAlign: 'right',
            marginTop: '25px', // More margin above total
        },
        totalAmountLabel: {
            fontWeight: 'normal',
            color: '#E5D0A5',
            fontSize: '17px',
        },
        totalAmountValue: {
            fontWeight: 'normal',
            color: '#E5D0A5',
            fontSize: '17px',
        },
        summaryIcon: {
            color: '#D4AF37', // Gold icon color
            fontSize: '15px', // Larger icon size
        },
        estimatedDeliveryBox: {
            backgroundColor: '#D4AF37',
            borderRadius: '8px',
            padding: '15px 20px', // Generous padding
            marginTop: '30px', // More margin above this box
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            color: '#000',
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign: 'center',
            justifyContent: 'center',
        },
        deliveryIcon: {
            color: '#000',
            fontSize: '15px',
        },
        // --- Bottom Action Buttons ---
        bottomButtonGroup: {
            display: 'flex',
            gap: '15px',
            marginTop: '30px', // More margin above buttons
            flexWrap: 'wrap',
            justifyContent: 'flex-end', // Align to right
        },
        bottomButton: {
            padding: '12px 25px', // Generous padding for buttons
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            whiteSpace: 'nowrap',
            flexGrow: isSmallMobile ? 1 : 0,
        },
        backToCartButton: {
            backgroundColor: '#333',
            color: '#E5D0A5',
            border: 'none',
        },
        backToCartButtonHover: {
            backgroundColor: '#555',
        },
        checkOrderButton: {
            backgroundColor: '#D4AF37',
            color: '#000',
            border: 'none',
        },
        checkOrderButtonHover: {
            backgroundColor: '#C0A020',
        },
    };

    if (isLoading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                backgroundColor: '#E5D0A5'
            }}>
                <ClipLoader color="#D4AF37" size={50} />
            </div>
        );
    }

    return (
        <div style={styles.mainWrapper}>
            <Toaster position="top-center" />
            
            <h1 style={styles.pageTitle}>Edit Order</h1>

            {/* Estimated Shipping Date Box */}
            <div style={styles.shippingDateBox}>
                Estimated shipping date: {estimatedDelivery}
            </div>

            <div style={styles.pageContent}>
                {/* Left Column - Details Form */}
                <div style={styles.column}>
                    {/* Personal Details Card */}
                    <div style={styles.card}>
                        <h2 style={styles.sectionTitle}>Personal Details</h2>
                        <div style={styles.formGrid}>
                            <div style={styles.formGroup}>
                                <label htmlFor="firstName" style={styles.inputLabel}>First Name</label>
                                <input 
                                    type="text" 
                                    id="firstName" 
                                    placeholder="First Name" 
                                    style={styles.textInput} 
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label htmlFor="lastName" style={styles.inputLabel}>Last Name</label>
                                <input 
                                    type="text" 
                                    id="lastName" 
                                    placeholder="Last Name" 
                                    style={styles.textInput} 
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label htmlFor="yourEmail" style={styles.inputLabel}>Your Email</label>
                                <input 
                                    type="email" 
                                    id="yourEmail" 
                                    placeholder="Email" 
                                    style={styles.textInput} 
                                    value={formData.yourEmail}
                                    onChange={handleInputChange}
                                    readOnly
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label htmlFor="phoneNumber" style={styles.inputLabel}>Phone number</label>
                                <input 
                                    type="text" 
                                    id="phoneNumber" 
                                    placeholder="Number" 
                                    style={styles.textInput} 
                                    value={formData.phoneNumber}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Shipping Details Card */}
                    <div style={styles.card}>
                        <h2 style={styles.sectionTitle}>Shipping Details</h2>
                        <div style={styles.formGrid}>
                            <div style={{ ...styles.formGroup, gridColumn: isMobile ? 'auto' : 'span 2' }}>
                                <label htmlFor="shippingAddress" style={styles.inputLabel}>Shipping Address</label>
                                <textarea 
                                    id="shippingAddress" 
                                    placeholder="Enter Address" 
                                    style={styles.textArea}
                                    value={formData.shippingAddress}
                                    onChange={handleInputChange}
                                ></textarea>
                            </div>
                            <div style={styles.formGroup}>
                                <label htmlFor="zipCode" style={styles.inputLabel}>Zip-Code</label>
                                <input 
                                    type="text" 
                                    id="zipCode" 
                                    placeholder="Zip-Code" 
                                    style={styles.textInput} 
                                    value={formData.zipCode}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label htmlFor="city" style={styles.inputLabel}>City</label>
                                <input 
                                    type="text" 
                                    id="city" 
                                    placeholder="City" 
                                    style={styles.textInput} 
                                    value={formData.city}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label htmlFor="country" style={styles.inputLabel}>Country</label>
                                <input 
                                    type="text" 
                                    id="country" 
                                    placeholder="Country" 
                                    style={styles.textInput} 
                                    value={formData.country}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label htmlFor="state" style={styles.inputLabel}>State</label>
                                <input 
                                    type="text" 
                                    id="state" 
                                    placeholder="State" 
                                    style={styles.textInput} 
                                    value={formData.state}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label htmlFor="town" style={styles.inputLabel}>Town</label>
                                <input 
                                    type="text" 
                                    id="town" 
                                    placeholder="Town" 
                                    style={styles.textInput} 
                                    value={formData.town}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label htmlFor="famousSpot" style={styles.inputLabel}>Famous Spot</label>
                                <input 
                                    type="text" 
                                    id="famousSpot" 
                                    placeholder="Famous Spot" 
                                    style={styles.textInput} 
                                    value={formData.famousSpot}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        {/* Security Message Box */}
                        <div style={styles.securityMessageBox}>
                            <FaShieldAlt style={styles.securityIcon} />
                            We adhere entirely to the data security standards of the payment card industry
                        </div>
                    </div>
                </div>

                {/* Right Column - Promo and Summary */}
                <div style={styles.column}>
                    {/* Have a Promo Code? */}
                    <div style={styles.promoCodeCard}>
                        <h2 style={styles.promoCodeHeader}>Have a Promo Code?</h2>
                        <div style={styles.promoInputGroup}>
                            <input
                                type="text"
                                placeholder="CODE.123"
                                style={styles.textInput}
                            />
                            <button
                                style={styles.applyButton}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.applyButtonHover.backgroundColor}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.applyButton.backgroundColor}
                            >
                                Apply
                            </button>
                        </div>
                    </div>

                    {/* Order Summary */}
                    <div style={styles.orderSummaryCard}>
                        <h2 style={styles.orderSummaryHeader}>Order Summary</h2>
                        <hr style={styles.summaryDivider} />

                        {/* Order Items */}
                        {orderItems.map((item, index) => (
                            <div key={index} style={styles.summaryRow}>
                                <span style={styles.summaryLabel}>
                                    {item.quantity}x {item.productId.name}
                                </span>
                                <span style={styles.summaryValue}>{(item.priceAtPurchase * item.quantity).toFixed(2)}</span>
                            </div>
                        ))}
                        <hr style={styles.summaryDivider} />

                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>
                                <FaFileAlt style={styles.summaryIcon} />
                                Sub Total:
                            </span>
                            <span style={styles.summaryValue}>{orderSummary.subTotal.toFixed(2)}</span>
                        </div>
                        <hr style={styles.summaryDivider} />

                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>
                                <FaPercentage style={styles.summaryIcon} />
                                Discount:
                            </span>
                            <span style={styles.summaryValue}>-{orderSummary.discount.toFixed(2)}</span>
                        </div>
                        <hr style={styles.summaryDivider} />

                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>
                                <FaTruck style={styles.summaryIcon} />
                                Delivery Charges:
                            </span>
                            <span style={styles.summaryValue}>{orderSummary.deliveryCharges.toFixed(2)}</span>
                        </div>
                        <hr style={styles.summaryDivider} />

                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>
                                <FaMoneyBillAlt style={styles.summaryIcon} />
                                Estimated Tax(15.5%):
                            </span>
                            <span style={styles.summaryValue}>{orderSummary.estimatedTax.toFixed(2)}</span>
                        </div>
                        <hr style={styles.summaryDivider} />

                        <div style={{ ...styles.summaryRow, ...styles.totalAmountRow }}>
                            <span style={styles.totalAmountLabel}>Total Amount</span>
                            <span style={styles.totalAmountValue}>{orderSummary.totalAmount.toFixed(2)}</span>
                        </div>

                        {/* Order Status */}
                        <div style={{ 
                            ...styles.estimatedDeliveryBox, 
                            backgroundColor: orderStatus === 'delivered' ? '#4CAF50' : 
                                           orderStatus === 'outForDelivery' ? '#FFC107' : 
                                           orderStatus === 'cancelled' ? '#F44336' : '#2196F3'
                        }}>
                            <FaTruck style={styles.deliveryIcon} />
                            Order Status: {orderStatus.charAt(0).toUpperCase() + orderStatus.slice(1)}
                        </div>

                        {/* Estimated Delivery Box */}
                        <div style={styles.estimatedDeliveryBox}>
                            <FaTruck style={styles.deliveryIcon} />
                            Estimated Delivery by {estimatedDelivery}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={styles.bottomButtonGroup}>
                        {submitError && (
                            <div style={{ 
                                color: '#ff4444', 
                                width: '100%', 
                                textAlign: 'center',
                                marginBottom: '10px'
                            }}>
                                {submitError}
                            </div>
                        )}
                        
                        <button
                            style={{ 
                                ...styles.bottomButton, 
                                ...styles.cancelButton,
                                // backgroundColor: isSubmitting ? '#555' : styles?.cancelButton?.backgroundColor
                            }}
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            onMouseOver={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor)}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor}
                        >
                            Cancel
                        </button>
                        
                        <button
                            style={{ 
                                ...styles.bottomButton, 
                                ...styles.updateButton,
                                // backgroundColor: isSubmitting ? '#C0A020' : styles.updateButton.backgroundColor
                            }}
                            onClick={handleUpdateOrder}
                            disabled={isSubmitting}
                            onMouseOver={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = styles.updateButtonHover.backgroundColor)}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = isSubmitting ? '#C0A020' : styles.updateButton.backgroundColor}
                        >
                            {isSubmitting ? (
                                <>
                                    <ClipLoader color="#000" size={16} />
                                    <span style={{ marginLeft: '8px' }}>Updating...</span>
                                </>
                            ) : 'Update Order'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}